import React, { useState } from 'react';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import { useAuth } from '../../context/AuthContext';
import { Check, Loader2, Sparkles, Gift } from 'lucide-react';
import { TIERS } from '../../constants/tiers';
import { annualSavingsMonths } from '../../constants/annualSavings';
import { PRICING_CONTENT } from '../../constants/pricingContent';
import { notifyError } from '../../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// iter 176 - Blueprint Only / Essentials / Guided bullets, tagline and
// description now come from constants/pricingContent.js. Free trial,
// community and connected continue to source from FEATURES below
// because they are not one-time Blueprint tiers.
const FEATURES = {
  free: [
    'Newcomers Board (post + reply)',
    'Community Board (post + reply)',
    'Local Events - RSVP and attend',
    'No card required',
  ],
  blueprint_only: PRICING_CONTENT.blueprint_only.bullets,
  essentials:     PRICING_CONTENT.essentials.bullets,
  guided:         PRICING_CONTENT.guided.bullets,
  community: [
    'Community Board (post + DM)',
    'Real-world events in 6 Mexico cities',
    'Housing Finder + Roommates',
    'Local Deals - 15+ Mexico expat discounts',
    'Cancel anytime',
  ],
  connected: [
    'Community - included',
    'Monthly expert Q&A sessions',
    'Connected-only events',
  ],
};

const TAGLINES = {
  blueprint_only: PRICING_CONTENT.blueprint_only.tagline,
  essentials:     PRICING_CONTENT.essentials.tagline,
  guided:         PRICING_CONTENT.guided.tagline,
};

const DESCRIPTIONS = {
  blueprint_only: PRICING_CONTENT.blueprint_only.description,
  essentials:     PRICING_CONTENT.essentials.description,
  guided:         PRICING_CONTENT.guided.description,
};

const TierCard = ({ tier, onChoose, loading, currentRole }) => {
  const isCurrent = currentRole === tier.id;
  const features = FEATURES[tier.id] || [];
  const tagline = TAGLINES[tier.id];
  const description = DESCRIPTIONS[tier.id] || tier.description;
  return (
    <article
      className={`bg-white border rounded-xl p-6 flex flex-col relative ${
        tier.primary ? 'border-[#E8A830] shadow-lg' : 'border-[#EFE6CF]'
      }`}
      data-testid={`upgrade-tier-${tier.id}`}
    >
      {tier.badge && (
        <span
          className="absolute -top-3 left-6 bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-[9px] tracking-[0.18em] font-bold px-3 py-1 rounded-full"
          data-testid={`upgrade-tier-${tier.id}-badge`}
        >
          {tier.badge}
        </span>
      )}

      <header className="mb-4">
        <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-1">{tier.name}</h2>
        {tagline && (
          <p className="font-body italic text-sm text-[#1A5C6E] mb-2" data-testid={`upgrade-tier-${tier.id}-tagline`}>
            {tagline}
          </p>
        )}
        <div className="flex items-baseline gap-1.5 mb-1" data-testid={`upgrade-tier-${tier.id}-price`}>
          <span className="font-display italic text-4xl text-[#0A1F2E]">
            ${tier.price}
          </span>
          <span className="font-mono uppercase text-[11px] tracking-[0.12em] text-[#0A1F2E]/55">
            / {tier.interval}
          </span>
        </div>
        {tier.priceAnnual && (
          <p className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#1A5C6E]">
            or ${tier.priceAnnual}/yr - {annualSavingsMonths(tier.id)} months free
          </p>
        )}
        <p
          className="font-body text-sm text-[#0A1F2E]/70 mt-3 leading-snug"
          data-testid={`upgrade-tier-${tier.id}-description`}
        >
          {description}
        </p>
      </header>

      <ul className="space-y-2 mb-6 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0A1F2E]/80">
            <Check className="w-4 h-4 text-[#2CA8A0] flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <button
          disabled
          className="w-full bg-[#F4ECD8] text-[#0A1F2E]/60 font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full cursor-default"
          data-testid={`upgrade-tier-${tier.id}-current`}
        >
          Current plan
        </button>
      ) : tier.comingSoon ? (
        <button
          disabled
          className="w-full bg-[#F4ECD8] text-[#0A1F2E]/70 font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full cursor-not-allowed border border-[#EFE6CF]"
          data-testid={`upgrade-tier-${tier.id}-coming-soon`}
        >
          Coming Soon
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onChoose(tier.id)}
          disabled={loading === tier.id}
          className={`w-full font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full transition disabled:opacity-50 ${
            tier.primary
              ? 'bg-[#E8A830] text-[#0A1F2E] hover:bg-[#d59824]'
              : 'bg-[#0A1F2E] text-white hover:bg-[#1A5C6E]'
          }`}
          data-testid={`upgrade-tier-${tier.id}-cta`}
        >
          {loading === tier.id ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : tier.cta}
        </button>
      )}
    </article>
  );
};

const PortalUpgrade = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);

  const choose = async (planId) => {
    setLoading(planId);
    try {
      const isSub = planId === 'community' || planId === 'connected';
      const r = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Backend (routes/mundalo_billing.py::CheckoutRequest) expects
          // `tier` + `billing_period`. Sending `plan` here returned a 422
          // validation error in production and surfaced as a cryptic
          // "Field required" Sonner toast on the upgrade modal.
          tier: planId,
          billing_period: isSub ? 'monthly' : 'onetime',
          success_path: '/portal/dashboard',
          cancel_path: '/portal/upgrade',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.url) {
        window.location.href = d.url;
      } else {
        notifyError(d, 'Could not start checkout. Please contact support.');
      }
    } catch {
      notifyError('Network error. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  // Render two rows: one-time + recurring
  const oneTimeTiers = [TIERS.blueprint_only, TIERS.essentials, TIERS.guided];
  const recurringTiers = [TIERS.community, TIERS.connected];

  return (
    <PortalLayout activeTab="upgrade">
      <SEO title="Upgrade - Mundalo" description="Choose your Mundalo plan." path="/portal/upgrade" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="portal-upgrade-page">
        <header className="mb-10 text-center max-w-2xl mx-auto">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#E8A830] mb-2 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Two journeys · one Mundalo
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            Get there. Stay there.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75">
            Pay once to plan the move. Pay monthly to live the life. Most members do both.
          </p>
        </header>

        <section className="mb-12">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#E8A830] mb-2">THE MOVE · One-time</p>
          <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-1">
            Trade six months of forums for one weekend of clarity.
          </h2>
          <p className="font-body text-sm text-[#0A1F2E]/70 mb-6 max-w-2xl">
            Pay once. Get the plan. Move with confidence. The longer you wait, the longer you stay where you don't want to be.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {oneTimeTiers.map((t) => (
              <TierCard key={t.id} tier={t} onChoose={choose} loading={loading} currentRole={user?.role} />
            ))}
          </div>
        </section>

        <section className="mb-12">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#2CA8A0] mb-2">THE LIFE · Monthly or annual</p>
          <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-1">
            The hardest part isn't the move. It's month three.
          </h2>
          <p className="font-body text-sm text-[#0A1F2E]/70 mb-6 max-w-2xl">
            The novelty fades. Your friends are 2,000 miles away. Mundalo's community is the difference between staying and going home.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
            {recurringTiers.map((t) => (
              <TierCard key={t.id} tier={t} onChoose={choose} loading={loading} currentRole={user?.role} />
            ))}
          </div>
        </section>

        <section className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl p-6 md:p-8 text-center max-w-3xl mx-auto" data-testid="upgrade-faq-strip">
          <p className="font-body text-sm text-[#0A1F2E]/75 leading-relaxed">
            Most members start with <strong className="text-[#0A1F2E]">Guided</strong> for the move,
            then add <strong className="text-[#0A1F2E]">Connected</strong> once they're on the ground.
            Cancel any monthly plan anytime. One-time plans never expire.
          </p>
        </section>

        <section
          className="mt-8 bg-white border border-[#EFE6CF] rounded-xl p-5 md:p-6 max-w-3xl mx-auto flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 text-center sm:text-left"
          data-testid="upgrade-redeem-strip"
        >
          <div className="w-12 h-12 rounded-full bg-[#FCF8EE] border border-[#EFE6CF] flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5 text-[#1A5C6E]" />
          </div>
          <div className="flex-1">
            <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1">
              Have a promo code?
            </p>
            <p className="font-body text-sm text-[#0A1F2E]/75">
              Unlock your tier instantly with a Mundalo promo code - no checkout required.
            </p>
          </div>
          <a
            href="/portal/redeem"
            className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-3 rounded-full transition shrink-0"
            data-testid="upgrade-redeem-cta"
          >
            Redeem a code
          </a>
        </section>
      </div>
    </PortalLayout>
  );
};

export default PortalUpgrade;
